import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, provider } from '../firebase';
import { createUserWithEmailAndPassword, signInWithPopup, updateProfile } from 'firebase/auth';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F7F5F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  card: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DDD6',
    borderRadius: '8px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '420px',
  },
  logo: {
    fontFamily: 'Playfair Display, Georgia, serif',
    fontSize: '28px',
    fontWeight: 400,
    letterSpacing: '-1px',
    textAlign: 'center',
    marginBottom: '0.5rem',
    display: 'block',
    textDecoration: 'none',
  },
  hostel: { color: '#111110' },
  chic: { color: '#4A9E8A' },
  subtitle: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#888880',
    textAlign: 'center',
    marginBottom: '2rem',
  },
  googleBtn: {
    width: '100%',
    padding: '11px',
    border: '1px solid #E0DDD6',
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#E0DDD6',
  },
  dividerText: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '12px',
    color: '#888880',
  },
  label: {
    display: 'block',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#444441',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E0DDD6',
    borderRadius: '4px',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    color: '#111110',
    backgroundColor: '#FFFFFF',
    marginBottom: '1rem',
    outline: 'none',
  },
  submitBtn: {
    width: '100%',
    padding: '11px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: '#4A9E8A',
    color: '#F7F5F0',
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '1.5rem',
  },
  error: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#E24B4A',
    marginBottom: '1rem',
    textAlign: 'center',
  },
  footer: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '13px',
    color: '#888880',
    textAlign: 'center',
  },
  footerLink: {
    color: '#4A9E8A',
    textDecoration: 'none',
  },
  terms: {
    fontFamily: 'DM Sans, system-ui, sans-serif',
    fontSize: '11px',
    color: '#888880',
    textAlign: 'center',
    marginBottom: '1.5rem',
    lineHeight: '1.5',
  },
};

function Signup() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName: username });
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  const handleGoogleSignup = async () => {
    setError('');
    try {
      await signInWithPopup(auth, provider);
      navigate('/');
    } catch (err) {
      setError('Google sign up failed. Please try again.');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Link to="/" style={styles.logo}>
          <span style={styles.hostel}>Hostel</span>
          <span style={styles.chic}>Chic</span>
        </Link>
        <p style={styles.subtitle}>Create your account</p>

        <button style={styles.googleBtn} onClick={handleGoogleSignup}>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3z"/>
          </svg>
          Continue with Google
        </button>

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine}></div>
        </div>

        {error && <p style={styles.error}>{error}</p>}

        <form onSubmit={handleSignup}>
          <label style={styles.label}>Username</label>
          <input
            style={styles.input}
            type="text"
            placeholder="How you'll appear on listings"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            placeholder="At least 6 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <label style={styles.label}>Confirm password</label>
          <input
            style={styles.input}
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <p style={styles.terms}>
            By signing up you agree to the HostelChic Terms of Service and Privacy Policy.
          </p>
          <button style={styles.submitBtn} type="submit">Create account</button>
        </form>

        <p style={styles.footer}>
          Already have an account?{' '}
          <Link to="/login" style={styles.footerLink}>Log in</Link>
        </p>
      </div>
    </div>
  );
}

export default Signup;